import { useCallback, useRef, useState } from "react";
import HistoricAnalysesAPI from "../../apis/historic-analyses/historicAnalysesAPI.js";
import { initialPreview } from "../../conts/defaultValues.js";

// Viewing a past analysis (on the map + its report preview).
// Kept completely apart from the current analysis.
// viewed = { item, data } where data = raw GET response
// (real_fire, simulated_fire, saved_zone, saved_buildings, burned_buildings)
const useAnalysisViewer = () => {
  const analysesHistoryApi = useRef(new HistoricAnalysesAPI()).current;
  const openRequest = useRef(0); // ignores late responses
  const previewRequest = useRef(0); // ignores late responses

  const [openingId, setOpeningId] = useState(null); // row being opened
  const [viewed, setViewed] = useState(null);
  const [reportPreview, setReportPreview] = useState(initialPreview);

  // ---------------------- open on the map ---------------------------
  const openAnalysis = useCallback(async (item) => {
    const requestId = ++openRequest.current;
    setOpeningId(item.analysis_id);
    try {
      const response = await analysesHistoryApi.getAnalysisByIdApi(item.analysis_id);
      if (requestId !== openRequest.current) return null;
      if (response.status !== 200) throw new Error("Unexpected status");
      const d = response.data;

      setViewed({ item, data: d });

      // returned so the template can zoom to the opened shapes
      return {
        type: "FeatureCollection",
        features: [
          ...(d.real_fire?.features ?? []),
          ...(d.simulated_fire?.features ?? []),
          ...(d.saved_zone?.features ?? []),
        ],
      };
    } catch (err) {
      console.error("Open analysis error:", err);
      return null;
    } finally {
      if (requestId === openRequest.current) setOpeningId(null);
    }
  }, []);

  const closeViewed = useCallback(() => setViewed(null), []);

  // ---------------------- report preview ---------------------------
  const closeReportPreview = useCallback(() => {
    previewRequest.current++;
    setReportPreview((prev) => {
      if (prev.data?.url) URL.revokeObjectURL(prev.data.url);
      return initialPreview;
    });
  }, []);

  const openReportPreview = useCallback(async (item) => {
    const requestId = ++previewRequest.current;
    setReportPreview((prev) => {
      if (prev.data?.url) URL.revokeObjectURL(prev.data.url);
      return { item, data: null, isLoading: true, error: null };
    });
    try {
      const response = await analysesHistoryApi.getReportByAnalysisIdApi(item.analysis_id);
      if (requestId !== previewRequest.current) return;
      if (response.status !== 200) throw new Error("Unexpected status");

      const blob = new Blob([response.data], { type: "application/pdf" });
      setReportPreview({
        item,
        data: { blob, url: URL.createObjectURL(blob), filename: `rapport-${item.reference}.pdf` },
        isLoading: false,
        error: null,
      });
    } catch (err) {
      if (requestId !== previewRequest.current) return;
      console.error("Report preview error:", err);
      setReportPreview({
        item,
        data: null,
        isLoading: false,
        error: "Impossible de charger le rapport.",
      });
    }
  }, []);

  // ---------------------- after a delete ---------------------------
  // closes the preview / map view if they show the deleted analysis
  const forgetAnalysis = useCallback(
    (item) => {
      if (reportPreview.item?.analysis_id === item.analysis_id) closeReportPreview();
      if (viewed?.item?.analysis_id === item.analysis_id) closeViewed();
    },
    [reportPreview.item, viewed, closeReportPreview, closeViewed],
  );

  return {
    openingId,
    viewed,
    openAnalysis,
    closeViewed,
    reportPreview,
    openReportPreview,
    closeReportPreview,
    forgetAnalysis,
  };
};

export default useAnalysisViewer;
